/** Auth context: Supabase session + verified app_metadata claims (role/tenant). */
import { getClaims } from '@gym/api-client';
import type { AppMetadataClaims, AppRole } from '@gym/shared';
import type { Session } from '@supabase/supabase-js';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { supabase } from './supabase.js';

export interface AuthValue {
  session: Session | null;
  claims: AppMetadataClaims | null;
  loading: boolean;
  signOut: () => Promise<void>;
  hasRole: (...roles: AppRole[]) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [session, setSession] = useState<Session | null>(null);
  const [claims, setClaims] = useState<AppMetadataClaims | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    void supabase.auth.getSession().then(async ({ data }) => {
      const c = data.session ? await getClaims(supabase) : null;
      if (!active) return;
      setSession(data.session);
      setClaims(c);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, s) => {
      const c = s ? await getClaims(supabase) : null;
      if (!active) return;
      setSession(s);
      setClaims(c);
      setLoading(false);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      session,
      claims,
      loading,
      signOut: async () => {
        await supabase.auth.signOut();
      },
      hasRole: (...roles: AppRole[]) => !!claims && roles.includes(claims.app_role),
    }),
    [session, claims, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
